/**
 * surprise.js — rare, always-positive surprises inside EXISTING flows.
 * Feature flag `surprise` (featureFlags.js). Rate: 1 in SURPRISE_EVERY meaningful
 * completions (counter persisted per device), never on the very first completion,
 * never a reward economy: surprises are facts / companion reactions / a nudge to a
 * real undiscovered item. Logs `surprise_triggered`; keeps counters for the guard-rails.
 */
import { flag } from "@/lib/featureFlags";
import { COUNTRIES, CONTINENTS } from "@/data/countries";
import { getCompanion } from "@/lib/companion";
import { PERSONALITY } from "@/lib/companionVoice";
import { nextDiscovery } from "@/lib/collections";
import { track } from "@/lib/track";
import { funnel } from "@/lib/learningFunnel";

export const SURPRISE_EVERY = 12;
const KEY = "mwq_surprise_v1";

function state() { try { return JSON.parse(localStorage.getItem(KEY) || "{}") || {}; } catch { return {}; } }
function save(s) { try { localStorage.setItem(KEY, JSON.stringify(s)); } catch { /* ignore */ } }

function pickFact() {
  const pool = [...COUNTRIES.filter((c) => c.funFact).map((c) => ({ text: c.funFact, label: c.name })), ...CONTINENTS.map((c) => ({ text: c.fact, label: c.name }))];
  return pool[Math.floor(Math.random() * pool.length)];
}

/** Call on every meaningful completion. Returns a surprise payload or null. */
export function maybeSurprise(context = {}) {
  if (!flag("surprise")) return null;
  const s = state();
  s.completions = (s.completions || 0) + 1;
  s.lastCompletionAt = Date.now();
  // Guard-rail: repeated completions within 20 s look like surprise-hunting → count but never trigger.
  const rapid = s.prevCompletionAt && Date.now() - s.prevCompletionAt < 20000;
  s.prevCompletionAt = Date.now();
  if (rapid) s.rapid = (s.rapid || 0) + 1;
  const due = s.completions > 1 && s.completions % SURPRISE_EVERY === 0 && !rapid;
  if (!due) { save(s); return null; }
  s.surprises = (s.surprises || 0) + 1;
  save(s);

  const c = getCompanion();
  const p = PERSONALITY[c.id] || PERSONALITY.compass;
  const nd = nextDiscovery();
  const roll = s.surprises % 3;
  let payload;
  if (roll === 0 && nd) payload = { kind: "surprise", variant: "discovery_hint", label: nd.collection, text: `${c.name} spotted something on the globe: ${nd.label} is still to discover.`, to: nd.to };
  else if (roll === 1) payload = { kind: "surprise", variant: "companion_quirk", label: c.name, text: `${c.name} ${p.quirk} — a tiny celebration just for you.` };
  else { const f = pickFact(); payload = { kind: "surprise", variant: "fact", label: f.label, text: f.text }; }
  track("surprise_triggered", { variant: payload.variant, completions: s.completions, ...(context.activityType ? { activityType: context.activityType } : {}) });
  funnel("activity_completed", { activityType: "surprise", reason: payload.variant });
  return payload;
}

/** Guard-rail metrics for the experiment (admin/debug). */
export function surpriseStats() {
  const s = state();
  return { completions: s.completions || 0, surprises: s.surprises || 0, rapidCompletions: s.rapid || 0, enabled: flag("surprise"), every: SURPRISE_EVERY };
}
